import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/lib/auth";
import { I18nProvider, useI18n } from "@/lib/i18nContext";
import MainLayout from "@/components/MainLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import PageviewTracker from "@/components/PageviewTracker";

import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import ExperiencePage from "@/pages/ExperiencePage";
import BlogListPage from "@/pages/BlogListPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ContactPage from "@/pages/ContactPage";

import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPostEditor from "@/pages/admin/AdminPostEditor";
import AdminStats from "@/pages/admin/AdminStats";
import AdminContent from "@/pages/admin/AdminContent";

const LANGS = ["", "en", "fr"]; // "" = German default (no prefix)

function NotFound() {
  const { t, pathWithLang } = useI18n();
  return (
    <div className="max-w-[800px] mx-auto px-6 py-32 text-center" data-testid="not-found">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--gold)] mb-4">404</div>
      <h1 className="font-display text-5xl mb-4">{t("notFound.title")}</h1>
      <a href={pathWithLang("/")} className="link-gold">{t("notFound.back")}</a>
    </div>
  );
}

const SITE_ROUTES = (
  <>
    <Route index element={<HomePage />} />
    <Route path="ueber-mich" element={<AboutPage />} />
    <Route path="dienste" element={<ServicesPage />} />
    <Route path="erfahrung" element={<ExperiencePage />} />
    <Route path="blog" element={<BlogListPage />} />
    <Route path="blog/:slug" element={<BlogPostPage />} />
    <Route path="kontakt" element={<ContactPage />} />
    <Route path="*" element={<NotFound />} />
  </>
);

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <I18nProvider>
              <PageviewTracker />
              <Routes>
                {/* German – no prefix */}
                <Route path="/" element={<MainLayout />}>{SITE_ROUTES}</Route>
                {/* English & French – language prefix */}
                {LANGS.filter(Boolean).map((lng) => (
                  <Route key={lng} path={`/${lng}`} element={<MainLayout />}>{SITE_ROUTES}</Route>
                ))}

                {/* Admin (language-agnostic) */}
                <Route path="/admin/login" element={<MainLayout />}>
                  <Route index element={<AdminLoginPage />} />
                </Route>
                <Route path="/admin" element={<MainLayout />}>
                  <Route index element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                  <Route path="stats" element={<ProtectedRoute><AdminStats /></ProtectedRoute>} />
                  <Route path="content" element={<ProtectedRoute><AdminContent /></ProtectedRoute>} />
                  <Route path="posts/new" element={<ProtectedRoute><AdminPostEditor /></ProtectedRoute>} />
                  <Route path="posts/:id" element={<ProtectedRoute><AdminPostEditor /></ProtectedRoute>} />
                </Route>
              </Routes>
            </I18nProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
